import React, { useState } from "react";
import { usePermission } from "../../hooks/usePermission";
import { useClubTenderActions } from "../../hooks/clubtender/useClubTenderActions";
import RequestDraftActions from "../club/RequestDraftActions";
import RequestPreviewModal from "../common/RequestPreviewModal";
import ArchiveRecord from "../club/ArchiveRecord";
import ReactivateRequestButton from "../club/ReactivateRequestButton";
import {
  isDraftRequest,
  isArchivedRequest,
} from "../../utils/tenderFormatters";

// The club list-view row's primary CTA button(s) — the SAME action the club
// TenderCard / StaffTenderCard shows for the active tab, reusing the shared
// useClubTenderActions handlers + the same permission gates:
//   My Requests (type='my-tenders') → "View pitches" (canViewPitches)
//   All Listed / Saved             → "Message Club" (canContactClub),
//                                     or "Apply Pitch" for staff-not-in-club.
// Rendered inside the clean list detail panel (button only, not the whole
// card), so the row stays a row but carries the card's action.
//
// Request drafts: a draft row has no pitches, so it carries the draft action
// set instead — the same RequestDraftActions component the card uses, wired to
// the same handlers from the same hook. The card/list toggle therefore cannot
// change what a draft can do.
const ClubListRowActions = ({
  tender,
  type,
  isStaffView = false,
  staffInClub = true,
  transferWindow = null,
  onDraftChanged = null,
}) => {
  const canViewPitches = usePermission("view_pitches");
  const canContactClub = usePermission("contact_club");
  const [showPreview, setShowPreview] = useState(false);
  const {
    loading,
    viewPitches,
    primaryAction,
    draftLoading,
    publishDraftNow,
    scheduleDraft,
    removeDraftSchedule,
    deleteDraft,
  } = useClubTenderActions(tender, {
    isStaffView,
    staffInClub,
    onChanged: onDraftChanged,
  });

  if (type === "my-tenders" && isArchivedRequest(tender)) {
    // The open list row of an archived request IS its record (the design's
    // open-row pattern): summary + accepted pitches render directly in the
    // expanded panel, no extra toggle, with Reactivate and the ended count
    // underneath — the same shared control the collapsed row carries.
    return (
      <div className="flex w-full flex-col gap-3">
        <ArchiveRecord tender={tender} variant="row" />
        <div className="flex flex-wrap items-center gap-3">
          <ReactivateRequestButton tender={tender} onChanged={onDraftChanged} />
          <div className="text-sm text-[#64748B]">
            {`Ended with ${tender.total_pitches ?? 0} pitches`}
          </div>
        </div>
      </div>
    );
  }

  if (type === "my-tenders" && isDraftRequest(tender)) {
    return (
      <>
        <RequestDraftActions
          tender={tender}
          transferWindow={transferWindow}
          isSaving={draftLoading}
          variant="row"
          onPreview={() => setShowPreview(true)}
          onPostNow={publishDraftNow}
          onSchedule={scheduleDraft}
          onRemoveSchedule={removeDraftSchedule}
          onDelete={deleteDraft}
        />
        {/* The real preview component, not a list-only copy. */}
        <RequestPreviewModal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          tender={tender}
          primaryAction={
            <button
              type="button"
              onClick={async () => {
                const ok = await publishDraftNow();
                if (ok !== false) setShowPreview(false);
              }}
              disabled={draftLoading}
              className={`w-full min-h-[44px] rounded-lg bg-[#2283FF] font-semibold text-white hover:bg-blue-600 ${
                draftLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {draftLoading ? "Working..." : "Post now"}
            </button>
          }
        />
      </>
    );
  }

  if (type === "my-tenders") {
    return canViewPitches ? (
      <button
        onClick={(e) => {
          e.stopPropagation();
          viewPitches();
        }}
        className="px-4 py-2 text-center bg-white border-2 border-[#2283FF] text-[#2283FF] font-medium rounded-lg hover:bg-[#E9F3FF]"
      >
        View pitches ({tender.total_pitches})
      </button>
    ) : (
      <div className="px-4 py-2 text-center border border-gray-200 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed">
        View pitches ({tender.total_pitches})
      </div>
    );
  }

  const disabled =
    loading || (!(isStaffView && !staffInClub) && !canContactClub);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        primaryAction();
      }}
      disabled={disabled}
      className={`px-4 py-2 text-center bg-[#2283FF] text-white font-medium rounded-lg ${
        disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-600"
      }`}
    >
      {loading
        ? isStaffView && !staffInClub
          ? "Processing..."
          : "Connecting..."
        : isStaffView && !staffInClub
          ? "Apply Pitch"
          : "Message Club"}
    </button>
  );
};

export default ClubListRowActions;
